import axios from 'axios';
import { refreshSession } from '../lib/refreshSession';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Create axios instance
const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  // Priorité: sessionStorage (impersonation) > localStorage (session normale)
  const impersonateToken = sessionStorage.getItem('impersonate_token');
  const normalToken = localStorage.getItem('token');
  const token = impersonateToken || normalToken;
  
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  // Bac à sable (formation) : bascule DB backend via en-tête dédié.
  try {
    if (localStorage.getItem('pitboard_sandbox') === '1') {
      config.headers['X-Pitboard-Sandbox'] = '1';
    }
  } catch (e) {
    /* ignore */
  }

  return config;
});

// Handle auth errors : tenter un refresh silencieux (S4) avant de déconnecter
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config;
    if (error.response?.status === 401 && original && !original._retry) {
      original._retry = true;
      const newToken = await refreshSession();
      if (newToken) {
        original.headers = original.headers || {};
        original.headers.Authorization = `Bearer ${newToken}`;
        return api(original);
      }
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      const adminPath = process.env.REACT_APP_ADMIN_PATH || 'gestion-csg';
      window.location.href = `/${adminPath}`;
    }

    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  register: (userData) => api.post('/auth/register', userData),
  getProfile: () => api.get('/auth/me'),
};

// S3 : récupère un token de téléchargement court (~2 min, scope file_download)
// à insérer dans les URLs de fichiers (?token=...) au lieu du token de session 30j.
// Helper centralisé (DRY) utilisé par tous les composants de download/preview.
export const getDownloadToken = async () => {
  const { data } = await api.post('/auth/download-token');
  return data.token;
};

// Documents API
export const documentsAPI = {
  upload: (formData) => api.post('/documents/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  list: () => api.get('/documents'),
  download: (documentId) => api.get(`/documents/${documentId}/download`, {
    responseType: 'blob'
  }),
};

// Quotes API
export const quotesAPI = {
  create: (quoteData) => api.post('/quotes', quoteData),
  list: () => api.get('/quotes'),
  get: (quoteId) => api.get(`/quotes/${quoteId}`),
};

// Blog API
export const blogAPI = {
  list: (category) => api.get('/blog', { params: { category } }),
  get: (slug) => api.get(`/blog/${slug}`),
};

// Testimonials API
export const testimonialsAPI = {
  list: () => api.get('/testimonials'),
  create: (testimonialData) => api.post('/testimonials', testimonialData),
};

// Appointments API
export const appointmentsAPI = {
  create: (appointmentData) => api.post('/appointments', appointmentData),
  list: () => api.get('/appointments'),
};

// Contact API
export const contactAPI = {
  send: (messageData) => api.post('/contact', messageData),
};

// Admin API
export const adminAPI = {
  getStats: () => api.get('/admin/stats'),
  getAllQuotes: () => api.get('/admin/quotes'),
  updateQuote: (quoteId, updates) => api.patch(`/admin/quotes/${quoteId}`, updates),
  
  // User management
  getAllUsers: (role = null) => api.get(`/admin/users${role ? `?role=${role}` : ''}`),
  updateUser: (userId, updates) => api.put(`/admin/users/${userId}`, updates),
  deleteUser: (userId) => api.delete(`/admin/users/${userId}`),
  createAdmin: (adminData) => api.post('/admin/create-admin', adminData),
};

// Partner API
export const partnerAPI = {
  getClients: () => api.get('/partner/clients'),
  getQuotes: () => api.get('/partner/quotes'),
};

// Client API
export const clientAPI = {
  getDashboard: () => api.get('/client/dashboard'),
  updateProfile: (profileData) => api.put('/client/profile', profileData),
  getContracts: () => api.get('/client/contracts'),
  getMessages: () => api.get('/client/messages'),
  sendMessage: (messageData) => api.post('/client/messages', messageData),
  getDocuments: () => api.get('/client/documents'),
  uploadDocument: (formData) => api.post('/client/documents/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  getPracticalNumbers: () => api.get('/client/practical-numbers'),
  
  // Collaborators management
  getCollaborators: () => api.get('/client/collaborators'),
  createCollaborator: (collaboratorData) => api.post('/client/collaborators/create', collaboratorData),
  extendCollaboratorAccess: (collaboratorId, duration) => api.put(`/client/collaborators/${collaboratorId}/extend`, duration),
  updateCollaboratorPermissions: (collaboratorId, permissions) => api.put(`/client/collaborators/${collaboratorId}/permissions`, permissions),
  suspendCollaborator: (collaboratorId) => api.put(`/client/collaborators/${collaboratorId}/suspend`),
  reactivateCollaborator: (collaboratorId) => api.put(`/client/collaborators/${collaboratorId}/reactivate`),
  deleteCollaborator: (collaboratorId) => api.delete(`/client/collaborators/${collaboratorId}`),
  regenerateCollaboratorLink: (collaboratorId) => api.post(`/client/collaborators/${collaboratorId}/regenerate-link`),
  
  // Companies
  getCompanies: () => api.get('/client/companies'),
};

export default api;